import { useCallback, useEffect, useState, useRef, ReactNode } from "react"
import { TouchState, MobileSwiperProps } from "../../types/helpers/mobileSwiper"

export default function MobileSwiper({ children, onSwipe }: MobileSwiperProps) {
  const wrapperRef = useRef(null)
  const [touchState, setTouchState] = useState<TouchState>({
    startX: 0,
    startY: 0,
    isSwiping: false,
    isAnimating: false,
    swipeDirection: null
  })

  const SWIPE_THRESHOLD = 50 // Minimum distance for a swipe
  const DIRECTION_LOCK_THRESHOLD = 10 // Distance before locking swipe direction
  const ANIMATION_DURATION = 100 // Duration in ms to prevent rapid swipes

  const handleTouchStart = useCallback((e) => {
    if (!wrapperRef.current?.contains(e.target)) return

    setTouchState(prev => ({
      ...prev,
      startX: e.touches[0].clientX,
      startY: e.touches[0].clientY,
      isSwiping: true,
      swipeDirection: null
    }))
  }, [])

  const handleTouchMove = useCallback((e) => {
    if (!touchState.isSwiping) return

    const currentX = e.touches[0].clientX
    const currentY = e.touches[0].clientY
    const deltaX = currentX - touchState.startX
    const deltaY = currentY - touchState.startY

    // Determine and lock swipe direction
    if (!touchState.swipeDirection) {
      if (Math.abs(deltaX) > DIRECTION_LOCK_THRESHOLD || Math.abs(deltaY) > DIRECTION_LOCK_THRESHOLD) {
        const direction = Math.abs(deltaX) > Math.abs(deltaY) ? 'horizontal' : 'vertical'
        setTouchState(prev => ({ ...prev, swipeDirection: direction }))
      }
    }

    // If horizontal swipe is locked, prevent vertical scrolling
    if (touchState.swipeDirection === 'horizontal') {
      e.preventDefault()
    }
  }, [touchState.isSwiping, touchState.startX, touchState.startY, touchState.swipeDirection])

  const handleTouchEnd = useCallback((e) => {
    if (!touchState.isSwiping || touchState.isAnimating) return

    const endX = e.changedTouches[0].clientX
    const deltaX = endX - touchState.startX

    // Only trigger swipe if we're past threshold and locked to horizontal
    if (Math.abs(deltaX) >= SWIPE_THRESHOLD && touchState.swipeDirection === 'horizontal') {
      setTouchState(prev => ({ ...prev, isAnimating: true }))

      onSwipe({ deltaX })

      // Prevent additional swipes during animation
      setTimeout(() => {
        setTouchState(prev => ({ ...prev, isAnimating: false }))
      }, ANIMATION_DURATION)
    }

    setTouchState(prev => ({
      ...prev,
      isSwiping: false,
      swipeDirection: null
    }))
  }, [touchState.isSwiping, touchState.isAnimating, touchState.startX, touchState.swipeDirection, onSwipe])

  useEffect(() => {
    const wrapper = wrapperRef.current
    if (!wrapper) return

    wrapper.addEventListener("touchstart", handleTouchStart, { passive: false })
    wrapper.addEventListener("touchmove", handleTouchMove, { passive: false })
    wrapper.addEventListener("touchend", handleTouchEnd, { passive: false })

    return () => {
      wrapper.removeEventListener("touchstart", handleTouchStart)
      wrapper.removeEventListener("touchmove", handleTouchMove)
      wrapper.removeEventListener("touchend", handleTouchEnd)
    }
  }, [handleTouchStart, handleTouchMove, handleTouchEnd])

  return (
    <div
      cy-test-id="mobile-swiper"
      ref={wrapperRef}
      style={{
        touchAction: touchState.swipeDirection === 'horizontal' ? 'none' : 'auto',
        userSelect: 'none'
      }}
    >
      {children}
    </div>
  )
}